import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GlobalService } from './global.service';
import { Router } from '@angular/router';
import { firstValueFrom, Observable } from 'rxjs';
import environment from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  // base_url = 'http://localhost:3000'
  base_url = environment.url;

  constructor(
    public http: HttpClient,
    public gs: GlobalService,
    public router: Router,
  ) {
    // Check if running on localhost and override base_url
    // if (window.location.origin == 'http://localhost:4200') {
    //   this.base_url = 'http://localhost:3000';
    // } else {
    // }
    this.base_url = environment.url;
  }

  token = this.gs?.user_data?.token;
  private session_expired = false;

  async get(url: string, query_params: any) {
    this.token = this.gs?.user_data?.token;
    try {
      let params = { ...query_params };
      params = this.removeEmptyValues(params);
      const headers = {
        Authorization: `Bearer ${this.token}`,
      };
      const response: any = await firstValueFrom(
        this.http.get(this.base_url + url, { params, headers }),
      );
      return response;
    } catch (error: any) {
      this.route_for_401(error);
      if (error.status === 401) return;
      throw error;
    }
  }

  async delete(url: string, query_params: any) {
    try {
      this.token = this.gs?.user_data?.token;
      let params = { ...query_params };
      const headers = {
        Authorization: `Bearer ${this.token}`,
      };
      let response: any = await firstValueFrom(
        this.http.delete(this.base_url + url, { params, headers }),
      );
      return response;
    } catch (error: any) {
      this.route_for_401(error);
      if (error.status === 401) return;
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }

  async post(url: string, query_params: any, body: any) {
    try {
      this.token = this.gs?.user_data?.token;
      let params = { ...query_params };
      if (url.includes('set-password') || url.includes('forgot-password')) {
        let response: any = await firstValueFrom(
          this.http.post(this.base_url + url, body, { params: params }),
        );
        return response;
      }
      const headers = {
        Authorization: `Bearer ${this.token}`,
      };
      let response: any = await firstValueFrom(
        this.http.post(this.base_url + url, body, { params, headers }),
      );
      return response;
    } catch (error: any) {
      // Login itself returning 401 means wrong credentials, not an expired
      // session — let the caller see the real backend message instead of
      // being swallowed by route_for_401's "Session Expired" handling.
      if (url.includes('/user/log-in')) {
        throw error;
      }
      this.route_for_401(error);
      if (error.status === 401) return;
      throw error;
    }
  }

  async put(url: string, body: any) {
    try {
      this.token = this.gs?.user_data?.token;
      const headers = {
        Authorization: `Bearer ${this.token}`,
      };
      let response: any = await firstValueFrom(
        this.http.put(this.base_url + url, body, { headers })
      );
      return response;
    } catch (error: any) {
      this.route_for_401(error);
      if (error.status === 401) return;
      throw error;
    }
  }

  removeEmptyValues(data: any) {
    let cleanObject = (obj: any) => {
      for (const [key, value] of Object.entries(obj)) {
        if (value === null || value === '' || value === undefined) {
          delete obj[key];
        } else if (typeof value === 'object') {
          this.removeEmptyValues(value);
          if (Object.keys(value).length === 0 && !Array.isArray(value)) {
            delete obj[key];
          } else if (Array.isArray(value) && value.length === 0) {
            delete obj[key];
          }
        }
      }
    };

    let cleanArray = (arr: any[]) => {
      for (let i = 0; i < arr.length; i++) {
        const value = arr[i];
        if (value === null || value === '' || value === undefined) {
          arr.splice(i, 1);
        } else if (typeof value === 'object') {
          this.removeEmptyValues(value);

          if (Object.keys(value).length === 0 && !Array.isArray(value)) {
            arr.splice(i, 1);
            i--;
          } else if (Array.isArray(value) && value.length === 0) {
            arr.splice(i, 1);
            i--;
          }
        }
      }
    };

    if (Array.isArray(data)) {
      cleanArray(data);
    } else if (typeof data === 'object' && data !== null) {
      cleanObject(data);
    }
    return data;
  }

  customUpload(url: string, file: File): Observable<any> {
    return new Observable((observer) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // const base64String = reader.result as string;
        // // console.log(file)
        // You can now use the base64String in your POST request
        // const formData = {
        //   file: base64String,
        //   file_name: file.name,
        //   size: file.size,
        // };
        //
        let formData = {
          content_type: file.type,
          file_extension: file.name.split('.').pop(),
          file_size: file.size,
        };

        this.post('/attachment/upload-attachment', formData, {}).then(
          (data: any) => {
            const formData = new FormData();
            formData.append('file', file);
            const headers = new HttpHeaders({
              'Content-Type': file.type,
            });
            this.http.put(url, formData, { headers }).subscribe(
              (response) => {
                observer.next(response);
                observer.complete();
              },
              (error) => {
                observer.error(error);
                observer.complete();
              },
            );
          },
        );
      };

      reader.readAsDataURL(file);
    });
  }

  async gets(url: string, query_params: any, body: any) {
    try {
      let params = { ...query_params };
      const headers = {
        Authorization: `Bearer ${this.token}`,
      };
      let response: any = await firstValueFrom(
        this.http.post(this.base_url + url, body, { params, headers }),
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  route_for_401(error: any): void {
    if (error.status === 401 && !this.session_expired) {
      this.session_expired = true;
      this.gs.toastr_shows_function(
        'Session Expired. Please login again.',
        'Unauthorized',
        'error',
      );
      this.gs.user_data = {};
      localStorage.clear();
      this.router.navigate(['/sign-in']);
    }
  }

  reset_session() {
    this.session_expired = false;
  }
}
